import React from 'react';
import { DatelineBlock } from '@/declarations/models/blocks/DatelineBlock';
import { BlockPreviewComponent } from '@/editor/lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { apiTimestampToLocalDate, formatAPITimestampLocal } from '@/utils/dates';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { Page } from '@/declarations/models/Page';

export const DatelineBlockPreview: BlockPreviewComponent = () => {
  const { block } = useCurrentBlock();
  const currentBlock = block as DatelineBlock;
  const publishtime = useWatch<Page, 'published_at'>({
    name: 'published_at',
  });
  const { t } = useTranslation('components');
  // The page's last save. The portal shows when the published content was last changed.
  const updatedAt = useWatch<Page, 'updated_at'>({ name: 'updated_at' });

  // Short month names (e.g. «1. okt. 2026»), for both dates
  const dateFormat = currentBlock.showTime ? 'datetime' : 'date';

  // Only shown when it reads later than the publication: a later (local) day when only dates are shown, a later
  // minute when the time is shown too. Publishing saves the content seconds after the publication time.
  const publishedDate = apiTimestampToLocalDate(publishtime);
  const modifiedDate = apiTimestampToLocalDate(updatedAt);
  const shownPrecision = (d: Date) =>
    currentBlock.showTime
      ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes()).getTime()
      : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const showLastModified =
    !!currentBlock.showLastModified &&
    !!modifiedDate &&
    (!publishedDate || shownPrecision(modifiedDate) > shownPrecision(publishedDate));

  // TODO: Placeholder. align with base2.css.
  return (
    <div className='article__grid'>
      <div className='article__metadata cfz'>
        <div className='module__content'>
          <i
            className='module__icon i-icon-time'
            style={{ marginRight: '5px', display: 'inline-block', fontSize: '1em' }}
          />
          {currentBlock.text && <strong>{currentBlock.text}</strong>}{' '}
          {publishtime && <time dateTime={publishtime}>{formatAPITimestampLocal(publishtime, dateFormat)}</time>}
          {showLastModified && (
            <>
              {/* The portal's separator between the two dates */}
              {publishtime && (
                <span className='article__datesep' aria-hidden='true'>
                  &nbsp;·&nbsp;
                </span>
              )}
              <span className='article__lastmodified'>
                {currentBlock.lastModifiedText || t('DatelineBlock.LastModifiedDefaultText')}{' '}
                <time dateTime={updatedAt}>{formatAPITimestampLocal(updatedAt, dateFormat)}</time>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
